import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  loginWithCredentials,
  loginWithGoogle as apiLoginWithGoogle,
  getUser,
  bindReferralCode,
} from '@lib/services/auth/authApi';
import { getFpr, clearFpr } from '@lib/utils/referralStorage';
import {
  createTrainingNotificationService,
  type TrainingNotificationService,
  type TrainingStatusUpdate,
  type TrainingNotification,
  defaultTrainingStatusHandler,
} from '@lib/services/training/trainingNotificationService';
import { type UserProfile, type LoginResponse } from '../types/auth';
import { useGlobalStore } from './globalStore';
import { useSupportPerformanceStore } from './supportPerformanceStore';
import { useInsightsStore } from './insightsStore';
import { useOverviewStore } from './overviewStore';
import { useConversationStore } from './conversationStore';
import { useAgentChatStore } from './agentChatStore';
import { useCustomisationStore } from './customisationStore';
import { useProductScanStore } from './productScanStore';
import { useOnboardingStore } from './onboardingStore';
import { useQuestionnaireStore } from './questionnaireStore';
import { useBusiestTimeStore } from './busiestTimeStore';
import { useCreditStore } from './creditStore';
import { usePlanApprovalStore } from './planApprovalStore';

const TEMP_PASSWORD_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_TEMP_PASSWORD_KEY = 'temp_auth_session';

class TempPasswordManager {
  private static instance: TempPasswordManager;
  private tempData: {
    email: string;
    password: string;
    expiry: number;
  } | null = null;

  static getInstance(): TempPasswordManager {
    if (!TempPasswordManager.instance) {
      TempPasswordManager.instance = new TempPasswordManager();
    }
    return TempPasswordManager.instance;
  }

  setTempCredentials(email: string, password: string): void {
    const expiry = Date.now() + TEMP_PASSWORD_EXPIRY_MS;
    this.tempData = { email, password, expiry };

    try {
      sessionStorage.setItem(
        SESSION_TEMP_PASSWORD_KEY,
        JSON.stringify({
          email,
          password, // Store password in sessionStorage for page reload
          expiry,
          hasTemp: true,
        })
      );
    } catch (error) {
      console.warn('Session storage not available:', error);
    }
  }

  getTempCredentials(): { email: string; password: string } | null {
    // Try to restore from sessionStorage if in-memory data is not available
    if (!this.tempData) {
      try {
        const sessionData = sessionStorage.getItem(SESSION_TEMP_PASSWORD_KEY);
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          if (parsed.hasTemp && parsed.password && Date.now() <= parsed.expiry) {
            // Restore to memory
            this.tempData = {
              email: parsed.email,
              password: parsed.password,
              expiry: parsed.expiry,
            };
          }
        }
      } catch (error) {
        console.warn('Failed to restore temp credentials from session:', error);
      }
    }

    if (!this.tempData) {
      return null;
    }

    // Check if expired
    if (Date.now() > this.tempData.expiry) {
      this.clearTempCredentials();
      return null;
    }

    return {
      email: this.tempData.email,
      password: this.tempData.password,
    };
  }

  clearTempCredentials(): void {
    this.tempData = null;
    try {
      sessionStorage.removeItem(SESSION_TEMP_PASSWORD_KEY);
    } catch (error) {
      console.warn('Session storage not available:', error);
    }
  }

  hasTempCredentialsForEmail(email: string): boolean {
    if (this.tempData && this.tempData.email === email) {
      return Date.now() <= this.tempData.expiry;
    }

    try {
      const sessionData = sessionStorage.getItem(SESSION_TEMP_PASSWORD_KEY);
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        return parsed.email === email && parsed.hasTemp && parsed.password && Date.now() <= parsed.expiry;
      }
    } catch (error) {
      console.warn('Session storage not available:', error);
    }

    return false;
  }
}

interface AuthState {
  user: UserProfile | null;
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean; // Add hydration state
  error: string | null;
  isIntegrationInProgress: boolean;
  // External integration flow state
  isExternalIntegrationFlow: boolean;
  isFromLogin: boolean; // Flag to track if user just logged in
  trainingNotificationService: TrainingNotificationService | null;
  isTrainingListenerActive: boolean;
  completedTrainingBatches: Set<number>;
  errorTrainingBatches: Set<number>;
  login: (
    email: string,
    password: string,
    options?: { suppressVerificationRedirect?: boolean }
  ) => Promise<boolean>;
  loginWithGoogle: (code: string) => Promise<boolean>;
  /**
   * The sequence every "we already have tokens, finish signing this person in"
   * caller needs: setTokens → getUser(id) best-effort (falls back to `auth.user`
   * on failure) → authenticate with the full profile → tryAutoBindReferral →
   * training listener. Extracted from loginWithGoogle (which calls it once it has
   * a Google response) so the `/auth/shopify` handoff page's `signed_in` action
   * can run the identical sequence instead of re-deriving it.
   */
  completeExternalLogin: (auth: LoginResponse) => Promise<void>;
  /**
   * The non-redirecting part of `logout()`: stop the training listener, clear temp
   * credentials, reset every user-scoped store, clear localStorage, tear down the
   * chat widget, and null out tokens/user/isAuthenticated. Extracted so a caller
   * that needs to flush a previous account's data WITHOUT leaving the page it is
   * on (the `/auth/shopify` handoff page, switching accounts or discovering its
   * local session is dead) can do so — `logout()` keeps calling this and then
   * additionally hard-redirects to `/auth/login`.
   */
  resetUserScopedState: () => Promise<void>;
  loginWithGooglePending: (code: string) => Promise<{ user: UserProfile; tokens: { idToken: string; accessToken: string; refreshToken: string } } | false>;
  completeGoogleLogin: (user: UserProfile, tokens: { idToken: string; accessToken: string; refreshToken: string }) => void;
  logout: () => Promise<void>;
  setUser: (user: UserProfile) => void;
  setToken: (token: string) => void;
  setTokens: (tokens: { idToken: string; accessToken: string; refreshToken: string }) => void;
  clearError: () => void;
  setTempCredentials: (email: string, password: string) => void;
  getTempCredentials: () => { email: string; password: string } | null;
  clearTempCredentials: () => void;
  hasTempCredentialsForEmail: (email: string) => boolean;
  setIntegrationInProgress: (inProgress: boolean) => void;
  setExternalIntegrationFlow: (isExternal: boolean) => void;
  startExternalIntegrationFlow: () => void;
  completeExternalIntegrationFlow: () => void;
  updateUserFromOnboarding: (userProfile: UserProfile) => void;
  updateUserFromIntegrationActivation: (userProfile: UserProfile, platform: string) => void;
  updateUserFromPaymentNotification: (userProfile: UserProfile) => void;
  refreshUserFromServer: () => Promise<UserProfile | null>;
  setIsFromLogin: (isFromLogin: boolean) => void;
  startTrainingListener: () => void;
  stopTrainingListener: () => void;
  updateTrainingStatus: (status: TrainingStatusUpdate, notification?: TrainingNotification) => void;
}


/**
 * Auto-bind FirstPromoter referral code on login/signup success.
 * Reads pending fpr from localStorage and calls bind-referral if user is not yet bound.
 * Fire-and-forget — failures are logged but do not affect main flow.
 * Users who fail auto-binding can manually bind via the PlanDetailsPage UI.
 */
const tryAutoBindReferral = (user: UserProfile): void => {
  try {
    // Skip if user already has fpr bound
    if (user.fpRefId) {
      return;
    }
    const pendingFpr = getFpr();
    if (!pendingFpr) {
      return;
    }
    if (!user.id) {
      return;
    }

    bindReferralCode(user.id, pendingFpr)
      .then((updatedUser) => {
        console.log(`[authStore] Auto-bound referral code for user ${user.id}`);
        clearFpr();
        // Sync updated user (with fpRefId) into store
        useAuthStore.setState({ user: updatedUser });
      })
      .catch((err: any) => {
        console.warn('[authStore] Auto-bind referral failed (non-blocking):', err?.message || err);
        if (err?.subtype === 'invalid_referral_code') {
          // The landing URL carried a code no promoter owns. Keeping it would retry it on every
          // login and — worse — keep suppressing the manual card, which skips itself while any
          // pending fpr exists. The merchant would have no way left to enter a good one.
          //
          // Only if it is still the code we asked about: another tab, or a second landing hit
          // during this request, may have replaced it with a good one that deserves its turn.
          if (getFpr() === pendingFpr) {
            clearFpr();
          }
          return;
        }
        // Anything else (offline, 500, FirstPromoter unreachable) may still succeed later, so the
        // code stays put and the user can retry manually on PlanDetailsPage.
      });
  } catch (error) {
    console.warn('[authStore] tryAutoBindReferral threw:', error);
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const tempManager = TempPasswordManager.getInstance();

      return {
        user: null,
        idToken: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        isHydrated: false,
        error: null,
        isIntegrationInProgress: false,
        isExternalIntegrationFlow: false,
        isFromLogin: false,
        trainingNotificationService: null,
        isTrainingListenerActive: false,
        completedTrainingBatches: new Set(),
        errorTrainingBatches: new Set(),

        login: async (
          email: string,
          password: string,
          options?: { suppressVerificationRedirect?: boolean }
        ) => {
          set({ isLoading: true, error: null });
          try {
            const response = await loginWithCredentials(email, password);

            // Check if email verification is required
            if ('requiresVerification' in response && response.requiresVerification) {
              console.log('📧 Email verification required for:', response.email);
              set({ isLoading: false, error: null });

              // Store temp credentials for auto-login after verification
              tempManager.setTempCredentials(email, password);

              // Onboarding 2.0 password step (YEP-745): the account was JUST
              // confirmed at the verify step, but Cognito's confirmed state can
              // lag a beat, so login here can transiently report
              // requiresVerification. Hard-redirecting back to verify then caused
              // an infinite verify→password→verify loop (worse on slow mobile
              // networks). When the caller opts in, just return false so it can
              // retry — no navigation.
              if (options?.suppressVerificationRedirect) {
                return false;
              }

              // Navigate to register page with verification state
              window.location.href = `/auth/register?step=verify-email&email=${encodeURIComponent(response.email)}`;
              return false;
            }

            // Type guard to ensure we have a LoginResponse
            if (!('requiresVerification' in response)) {
              console.log('🔐 Login API Response:', response);

              // API returns tokens and user info directly
              if (response.idToken && response.user) {
                console.log('✅ Login successful, fetching full profile before authenticating');

                // WOR-1400 follow-up (the /plan-details "re-subscribe" flash): the login response's
                // user is missing the subscription fields the onboarding router reads (creditPlanCode
                // / effective plan). The moment `isAuthenticated` flips true, AuthLayout redirects
                // /auth/login → '/', and the root-route handoff computes a route from whatever user is
                // in the store. If that is the login response's user, a PAID account is read as unpaid
                // and flashes /plan-details before the full profile lands and bounces it to /home.
                //
                // So: make the token available (needed to fetch the profile) but DO NOT authenticate
                // yet — AuthLayout only redirects once `isAuthenticated` is true. Pull the full profile
                // first, THEN flip `isAuthenticated` with the complete user, so the first authenticated
                // render already knows the real plan and lands a paid user straight on /home.
                set({
                  idToken: response.idToken,
                  accessToken: response.accessToken,
                  refreshToken: response.refreshToken,
                });

                let fullUser = response.user;
                try {
                  fullUser = await getUser(response.user.id);
                } catch (e) {
                  // Best-effort: on a profile-fetch failure fall back to the login user rather than
                  // blocking sign-in. The worst case is the old flash, not a failed login.
                  console.error('❌ Failed to fetch full profile on login; using login user', e);
                }

                set({
                  user: fullUser,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null,
                  isFromLogin: true, // Set flag to indicate user just logged in
                });

                // Keep the product-scan store in sync, exactly as refreshUserFromServer does.
                if (fullUser.productScanResult) {
                  useProductScanStore.getState().updateFromUser(fullUser);
                }

                // Auto-bind FirstPromoter referral code if pending in localStorage
                tryAutoBindReferral(get().user ?? response.user);

                // Start training listener after successful login
                setTimeout(() => {
                  get().startTrainingListener();
                }, 1000);

                return true;
              } else {
                console.error('❌ Invalid login response structure:', response);
                throw new Error('Invalid response from server');
              }
            }
            
            // This should not be reached
            throw new Error('Unexpected response state');
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login failed';
            set({
              isLoading: false,
              error: errorMessage,
              isAuthenticated: false,
            });
            return false;
          }
        },

        loginWithGoogle: async (code: string) => {
          set({ isLoading: true, error: null });
          try {
            const response = await apiLoginWithGoogle(code);

            if (response.idToken && response.user) {
              await get().completeExternalLogin(response);
              return true;
            } else {
              throw new Error('Google login failed');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Google login failed';
            set({
              isLoading: false,
              error: errorMessage,
              isAuthenticated: false,
            });
            return false;
          }
        },

        completeExternalLogin: async (auth: LoginResponse) => {
          // Same fix as email/password login: fetch the full profile BEFORE flipping
          // `isAuthenticated`, so AuthLayout's redirect + the root-route handoff see the real plan
          // and a paid user does not flash the /plan-details ("re-subscribe") page on the way to
          // /home. Token first (to authenticate the fetch), authenticate second.
          set({
            idToken: auth.idToken,
            accessToken: auth.accessToken,
            refreshToken: auth.refreshToken,
          });

          let fullUser = auth.user;
          try {
            fullUser = await getUser(auth.user.id);
          } catch (e) {
            // Best-effort: on a profile-fetch failure fall back to the response's own user
            // rather than blocking sign-in. The worst case is the old /plan-details flash,
            // not a failed login.
            console.error('❌ Failed to fetch full profile after login; using response user', e);
          }

          set({
            user: fullUser,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isFromLogin: true, // Set flag to indicate user just logged in
          });

          if (fullUser.productScanResult) {
            useProductScanStore.getState().updateFromUser(fullUser);
          }

          // Auto-bind FirstPromoter referral code if pending in localStorage
          tryAutoBindReferral(get().user ?? auth.user);

          // Start training listener after successful login
          setTimeout(() => {
            get().startTrainingListener();
          }, 1000);
        },

        loginWithGooglePending: async (code: string) => {
          set({ isLoading: true, error: null });
          try {
            const response = await apiLoginWithGoogle(code);

            if (response.idToken && response.user) {
              // Don't set user state yet, return the data for integration processing
              return {
                user: response.user,
                tokens: {
                  idToken: response.idToken,
                  accessToken: response.accessToken,
                  refreshToken: response.refreshToken,
                },
              };
            } else {
              throw new Error('Google login failed');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Google login failed';
            set({
              isLoading: false,
              error: errorMessage,
              isAuthenticated: false,
            });
            return false;
          }
        },

        completeGoogleLogin: (user: UserProfile, tokens: { idToken: string; accessToken: string; refreshToken: string }) => {
          set({
            user: user,
            idToken: tokens.idToken,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isFromLogin: true,
          });

          // Auto-bind FirstPromoter referral code if pending in localStorage
          tryAutoBindReferral(user);

          // Start training listener after completing login
          setTimeout(() => {
            get().startTrainingListener();
          }, 1000);
        },

        resetUserScopedState: async () => {
          // Stop training listener before resetting
          get().stopTrainingListener();

          // Clear temporary credentials
          tempManager.clearTempCredentials();

          // Reset all user-scoped stores so account switches in the same tab
          // cannot see the previous account's data before the page reloads.
          useSupportPerformanceStore.getState().reset();
          useInsightsStore.getState().reset();
          useOverviewStore.getState().reset();
          useAgentChatStore.getState().resetAll();
          useConversationStore.getState().reset();
          useConversationStore.getState().clearAgentName();
          useCustomisationStore.getState().reset();
          useProductScanStore.getState().reset();
          useOnboardingStore.getState().reset();
          useQuestionnaireStore.getState().resetQuestionnaire();
          useBusiestTimeStore.getState().reset();
          // [SDD T3] The Shopify handoff account switch (ShopifyHandoffPage)
          // calls this WITHOUT a page reload, so without resetting these two the
          // previous account's credit overview (readOnly/requiresApproval) and
          // any open approval dialog would survive into the next account — the
          // wrong lock, and the "prompted" localStorage key would get written
          // under the NEW user's id while reflecting the OLD account's state.
          useCreditStore.getState().reset();
          usePlanApprovalStore.getState().close();

          // Clear persisted storage before state update
          localStorage.clear();

          const iframe = window.document.getElementById('chatbot-widget-iframe') as HTMLIFrameElement | null;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage('logout', '*');
            // eslint-disable-next-line no-self-assign
            iframe.src = iframe.src;
            console.log('Sent logout message to chat widget');
            // Wait 2 seconds for chat widget to process logout
            await new Promise(resolve => setTimeout(resolve, 2000));
          }

          // Remove chat widget
          const { chatWidgetManager } = await import('@lib/utils/chatWidgetManager');
          chatWidgetManager.removeChatWidget();

          set({
            user: null,
            idToken: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            isHydrated: true, // Ensure hydrated state is maintained
            isFromLogin: false, // Reset login flag
            // Reset training notification service state
            trainingNotificationService: null,
            isTrainingListenerActive: false,
            completedTrainingBatches: new Set(),
            errorTrainingBatches: new Set(),
          });
        },

        logout: async () => {
          await get().resetUserScopedState();

          // Hard reload to the login page. This nukes every module-level
          // singleton (Zustand stores, Firebase SDK, etc.), guaranteeing a
          // clean slate for the next account — any future store we add is
          // covered automatically.
          window.location.href = '/auth/login';
        },

        setUser: (user: UserProfile) => {
          set({ user, isAuthenticated: true });
          // Auto-bind FirstPromoter referral code if pending in localStorage and not yet bound
          tryAutoBindReferral(user);
        },

        setToken: (token: string) => {
          set({ idToken: token });
        },
        setTokens: (tokens: { idToken: string; accessToken: string; refreshToken: string }) => {
          set({
            idToken: tokens.idToken,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          });
        },

        clearError: () => {
          set({ error: null });
        },

        setTempCredentials: (email: string, password: string) => {
          tempManager.setTempCredentials(email, password);
        },

        getTempCredentials: () => {
          return tempManager.getTempCredentials();
        },

        clearTempCredentials: () => {
          tempManager.clearTempCredentials();
        },

        hasTempCredentialsForEmail: (email: string) => {
          return tempManager.hasTempCredentialsForEmail(email);
        },

        setIntegrationInProgress: (inProgress: boolean) => {
          set({ isIntegrationInProgress: inProgress });
        },

        setExternalIntegrationFlow: (isExternal: boolean) => {
          set({ isExternalIntegrationFlow: isExternal });
        },

        startExternalIntegrationFlow: () => {
          set({
            isExternalIntegrationFlow: true,
            isIntegrationInProgress: true,
          });
        },

        completeExternalIntegrationFlow: () => {
          set({
            isExternalIntegrationFlow: false,
            isIntegrationInProgress: false,
          });
        },

        updateUserFromOnboarding: (userProfile: UserProfile) => {
          set(state => {
            // Only update if user is authenticated and profile is valid
            if (!state.isAuthenticated || !userProfile?.id) {
              console.warn('Cannot update user profile: user not authenticated or invalid profile');
              return state;
            }

            // Merge the new profile data with existing user data
            const updatedUser: UserProfile = {
              ...state.user,
              ...userProfile,
            };

            console.log('User profile updated from onboarding:', updatedUser);
            return {
              ...state,
              user: updatedUser,
            };
          });
        },

        updateUserFromIntegrationActivation: (userProfile: UserProfile, platform: string) => {
          set(state => {
            // Only update if user is authenticated and profile is valid
            if (!state.isAuthenticated || !userProfile?.id) {
              console.warn('Cannot update user profile: user not authenticated or invalid profile');
              return state;
            }

            // Ensure the user IDs match for security
            if (state.user?.id !== userProfile.id) {
              console.error('User ID mismatch during integration activation update');
              return state;
            }

            // Merge the new profile data with existing user data
            const updatedUser: UserProfile = {
              ...state.user,
              ...userProfile,
            };

            console.log(
              `User profile updated from ${platform} integration activation:`,
              updatedUser
            );
            return {
              ...state,
              user: updatedUser,
            };
          });
        },

        updateUserFromPaymentNotification: (userProfile: UserProfile) => {
          set(state => {
            // Only update if user is authenticated and profile is valid
            if (!state.isAuthenticated || !userProfile?.id) {
              console.warn('Cannot update user profile: user not authenticated or invalid profile');
              return state;
            }

            // Ensure the user IDs match for security
            if (state.user?.id !== userProfile.id) {
              console.error('User ID mismatch during payment notification update');
              return state;
            }

            // Check if payment status has changed
            const currentPaymentStatus = state.user?.payment?.paymentStatus;
            const newPaymentStatus = userProfile?.payment?.paymentStatus;
            const hasPaymentStatusChanged = currentPaymentStatus !== newPaymentStatus;

            // Merge the new profile data with existing user data
            const updatedUser: UserProfile = {
              ...state.user,
              ...userProfile,
            };

            console.log('User profile updated from payment notification:', {
              updatedUser,
              hasPaymentStatusChanged,
              from: currentPaymentStatus,
              to: newPaymentStatus,
            });
            return {
              ...state,
              user: updatedUser,
            };
          });
        },

        refreshUserFromServer: async () => {
          const state = get();
          const { user, isAuthenticated } = state;

          if (!isAuthenticated || !user?.id) {
            console.warn('Cannot refresh user: not authenticated or no user ID');
            return null;
          }

          try {
            console.log('🔄 Refreshing user data from server for user:', user.id);
            const freshUserData = await getUser(user.id);

            set({ user: { ...freshUserData } });

            // Update productScanStore if available
            if (freshUserData.productScanResult) {
              const { useProductScanStore } = await import('./productScanStore');
              useProductScanStore.getState().updateFromUser(freshUserData);
            }

            // Start training listener after successful
            const state = get();
            if (state.isAuthenticated && state.user?.id && !state.isTrainingListenerActive) {
              setTimeout(() => {
                get().startTrainingListener();
              }, 500);
            }

            return freshUserData;
          } catch (error) {
            console.error('❌ Failed to refresh user data from server:', error);
            // Don't logout on refresh failure, just log the error
            return null;
          }
        },

        setIsFromLogin: (isFromLogin: boolean) => {
          set({ isFromLogin });
        },

        // Training notification methods
        startTrainingListener: () => {
          const state = get();

          if (!state.isAuthenticated || !state.user?.id) {
            console.warn('Cannot start training listener: user not authenticated or no user ID');
            return;
          }

          if (state.trainingNotificationService?.isListening()) {
            console.log('Training notification listener already active');
            return;
          }

          try {
            console.log('Starting training notification listener for user:', state.user.id);

            // Create training notification service
            const trainingService = createTrainingNotificationService({
              userId: state.user.id,
              onTrainingStatusUpdate: (
                status: TrainingStatusUpdate,
                notification: TrainingNotification
              ) => {
                console.log('Training status update received:', {
                  status,
                  notification: {
                    id: notification.id,
                    batchId: notification.batchId,
                    type: notification.type,
                    status: notification.status,
                    hasError: !!notification.errorMessage,
                  },
                });

                // Update the training status
                get().updateTrainingStatus(status, notification);

                defaultTrainingStatusHandler(status, notification);
              },
              onError: (error: Error) => {
                console.error('Training notification service error:', error);

                // Stop the listener on error
                set(state => ({
                  ...state,
                  isTrainingListenerActive: false,
                }));
              },
            });

            // Start listening
            trainingService.startListening();

            // Update state
            set(state => ({
              ...state,
              trainingNotificationService: trainingService,
              isTrainingListenerActive: true,
            }));

            console.log('Training notification listener started successfully');
          } catch (error) {
            console.error('Failed to start training notification listener:', error);
          }
        },

        stopTrainingListener: () => {
          const state = get();

          if (state.trainingNotificationService) {
            console.log('Stopping training notification listener');

            try {
              state.trainingNotificationService.stopListening();

              set(state => ({
                ...state,
                trainingNotificationService: null,
                isTrainingListenerActive: false,
              }));

              console.log('Training notification listener stopped successfully');
            } catch (error) {
              console.error('Error stopping training notification listener:', error);

              // Clear the state if stop listen failed
              set(state => ({
                ...state,
                trainingNotificationService: null,
                isTrainingListenerActive: false,
              }));
            }
          }
        },

        updateTrainingStatus: (
          status: TrainingStatusUpdate,
          notification?: TrainingNotification
        ) => {
          const state = get();
          const { user } = state;

          console.log('Updating training status:', status);

          switch (status) {
            case 'start':
              console.log('Training started');
              break;
            case 'update':
              console.log('Training progress update');
              break;
            case 'complete': {
              console.log('Training completed');

              if (notification?.batchId) {
                const currentState = get();

                // Check if this batch has error
                const hasError = currentState.errorTrainingBatches.has(notification.batchId);
                if (hasError) {
                  console.log(
                    `Training batch ${notification.batchId} has error, skipping success notification`
                  );
                  return;
                }

                const isAlreadyCompleted = currentState.completedTrainingBatches.has(
                  notification.batchId
                );

                if (isAlreadyCompleted) {
                  console.log(
                    `Training completion for batch ${notification.batchId} already processed, skipping global notification`
                  );
                  return;
                }

                // Mark this batch as completed
                set(state => ({
                  ...state,
                  completedTrainingBatches: new Set([
                    ...state.completedTrainingBatches,
                    notification.batchId,
                  ]),
                }));

                console.log(`Marking training batch ${notification.batchId} as completed`);

                // Show global notification
                if (user?.stepName?.includes('registerCompleteStep')) {
                  useGlobalStore.getState().addNotification({
                    type: 'success',
                    message:
                      'AI training completed successfully! Your chatbot is now ready with the updated knowledge.',
                  });
                }

                // Refresh knowledge base if user is on that page
                const currentPath = window.location.pathname;
                if (currentPath.includes('/knowledge')) {
                  // Dispatch a custom event to refresh the knowledge base page
                  window.dispatchEvent(new CustomEvent('trainingCompleted'));
                }

                setTimeout(() => get().refreshUserFromServer(), 2000);
              }
              break;
            }
            case 'error': {
              console.log('Training error occurred');

              if (notification?.batchId) {
                const currentState = get();

                // Check if error already processed for this batch
                const isAlreadyErrored = currentState.errorTrainingBatches.has(
                  notification.batchId
                );
                if (isAlreadyErrored) {
                  console.log(
                    `Training error for batch ${notification.batchId} already processed, skipping error notification`
                  );
                  return;
                }

                // Mark this batch as errored
                set(state => ({
                  ...state,
                  errorTrainingBatches: new Set([
                    ...state.errorTrainingBatches,
                    notification.batchId,
                  ]),
                }));

                console.log(`Marking training batch ${notification.batchId} as errored`);

                if (user?.stepName?.includes('registerCompleteStep')) {
                  // Show global error notification
                  const errorMessage =
                    'AI training failed. Please try again or contact support if the issue persists.';

                  useGlobalStore.getState().addNotification({
                    type: 'error',
                    message: errorMessage,
                  });
                }

                // Refresh knowledge base if user is on that page
                const currentPath = window.location.pathname;
                if (currentPath.includes('/knowledge')) {
                  // Dispatch a custom event to refresh the knowledge base page
                  window.dispatchEvent(new CustomEvent('trainingCompleted'));
                }

                setTimeout(() => get().refreshUserFromServer(), 2000);
              }

              break;
            }
          }
        },
      };
    },
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        idToken: state.idToken,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => state => {
        console.log('🔄 Auth store rehydrating from localStorage');
        if (state) {
          // Mark as hydrated
          state.isHydrated = true;

          // Initialize completedTrainingBatches set
          state.completedTrainingBatches = new Set();
          state.errorTrainingBatches = new Set();

          // Ensure isAuthenticated is set correctly based on stored data
          const hasValidToken = !!(state.idToken && state.user);
          console.log('💾 Restored auth state:', {
            hasUser: !!state.user,
            hasToken: !!state.idToken,
            storedIsAuthenticated: state.isAuthenticated,
            computedIsAuthenticated: hasValidToken,
          });

          // Force update isAuthenticated based on actual stored data
          if (hasValidToken && !state.isAuthenticated) {
            console.log('🔧 Fixing isAuthenticated state from localStorage');
            state.isAuthenticated = true;
          } else if (!hasValidToken && state.isAuthenticated) {
            console.log('🧹 Clearing invalid authentication state');
            state.isAuthenticated = false;
            state.user = null;
            state.idToken = null;
            state.accessToken = null;
            state.refreshToken = null;
          }

          // If user is authenticated, refresh user data from server
          if (hasValidToken) {
            console.log('🌐 Authenticated user found, refreshing data from server...');

            // Use setTimeout to avoid blocking the hydration process
            setTimeout(() => {
              state.refreshUserFromServer?.();
            }, 100);
          }
        } else {
          console.log('🆕 No stored auth state found, starting fresh');
        }
      },
    }
  )
);
